import React, { useMemo } from "react";

import { useChartData, UseChartDataParams } from "@/api/useChartData";
import { TiltAndSpinDataset } from "@/assets/TiltAndSpinVisualization";

import { TiltAndSpinChartCard } from ".";

/**
 * Subset of ChartsData type containing only the fields relevant for tilt and spin charts.
 * Properly types the tilt and spin data arrays to avoid casting and improve type safety.
 */
type ChartDataset = {
  pitch_type?: string | null;
  points_tilt_and_spin?: number[][] | null;
};

/**
 * Props for the TiltAndSpinChartDataCard component.
 *
 * This interface extends the base chart data parameters to provide
 * tilt and spin chart specific configuration.
 */
export interface TiltAndSpinChartDataCardProps extends UseChartDataParams {
  /** The title to display on the tilt and spin chart card */
  title?: string;
  /** Whether to show the legend - 'auto' shows legend if multiple datasets */
  showLegend?: true | false | "auto";
  /** Minimum spin value for the radar chart */
  minSpin?: number;
  /** Maximum spin value for the radar chart */
  maxSpin?: number;
  /** Children elements to render in the chart */
  children?: React.ReactNode;
  rings?: number[];
}

export const PitchTypeColors: Record<string, string> = {
  Fastball: "#FF0508",
  Splitter: "#032BFB",
  Curveball: "#084B12",
  Slider: "#E27528",
  Cutter: "#2DF206",
  Changeup: "#7E7E7E",
  Other: "#000000",
};
/**
 * TiltAndSpinChartDataCard Component
 *
 * A data-driven tilt and spin chart visualization component that fetches real player data
 * and displays it as a radar plot for baseball analytics.
 *
 * Features:
 * - Fetches live player chart data from the API
 * - Transforms backend data into tilt and spin format
 * - Shows loading states and error handling
 * - Supports various filters (result type, pitch type, etc.)
 *
 * @param props - Configuration for the tilt and spin chart including data parameters and display options
 * @returns A rendered tilt and spin chart card with player data
 *
 * @example
 * ```tsx
 * <TiltAndSpinChartDataCard
 *   title="Spin & Tilt"
 *   playerExternalId="player-123"
 *   startDate="2024-01-01"
 *   endDate="2024-12-31"
 *   gameType="game"
 *   playerType="pitcher"
 *   vsHandedness="all"
 *   chartType="tilt_and_spin"
 *   minSpin={1500}
 *   maxSpin={3000}
 * />
 * ```
 */
export const TiltAndSpinChartDataCard: React.FC<
  TiltAndSpinChartDataCardProps
> = ({
  title = "Tilt and Spin",
  showLegend = "auto",
  minSpin = 1500,
  maxSpin = 3000,
  playerExternalId,
  startDate,
  endDate,
  gameType,
  playerType,
  vsHandedness,
  chartType: _chartType,
  resultType,
  pitchType,
  filters,
  groupBy,
  rings,
}) => {
  const { data, isLoading, error } = useChartData({
    playerExternalId,
    startDate,
    endDate,
    gameType,
    playerType,
    vsHandedness,
    chartType: "tilt_and_spin",
    resultType,
    pitchType,
    filters,
    groupBy: groupBy || ["pitch_type"],
  });

  const datasets: TiltAndSpinDataset[] = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];

    // Transform the data into the format expected by TiltAndSpinChartCard
    return data.map((rawDataset) => {
      const dataset = rawDataset as ChartDataset;
      let points: { tilt: number; spin: number }[] = [];

      const tiltAndSpinData = dataset.points_tilt_and_spin;
      if (tiltAndSpinData && Array.isArray(tiltAndSpinData)) {
        points = tiltAndSpinData.map((coords: number[]) => ({
          tilt: coords[0],
          spin: coords[1],
        }));
      }

      return {
        name: dataset.pitch_type || "Other",
        color:
          PitchTypeColors[dataset.pitch_type || "Other"] ||
          PitchTypeColors.Other,
        points,
      };
    });
  }, [data]);

  if (error) {
    return (
      <div className="w-full min-h-[330px] h-full flex items-center justify-center bg-gray-50 rounded-lg">
        <p className="text-sm text-gray-500">Unable to load chart data</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <TiltAndSpinChartCard
        title={title}
        datasets={[]}
        showLegend={showLegend}
        minSpin={minSpin}
        maxSpin={maxSpin}
        rings={rings}
      >
        <div className="absolute inset-0 flex items-center justify-center bg-white/80">
          <div className="text-sm text-gray-500">Loading...</div>
        </div>
      </TiltAndSpinChartCard>
    );
  }

  return (
    <TiltAndSpinChartCard
      title={title}
      datasets={datasets}
      showLegend={showLegend}
      minSpin={minSpin}
      maxSpin={maxSpin}
      rings={rings}
    />
  );
};
